import { SSEConnection } from '@euler/lib/request/SSEClient';
import {
  CoachingCustomerMood,
  CoachingPracticeEnd,
  CoachingPracticeLine,
  CoachingPracticeSpeaker,
  CoachingPracticeTurnCallbacks,
} from '@euler/model/coaching-practice';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { PracticeEvent } from './practiceEvents';
import { PracticeScene } from './practiceScene';

export type PracticePhase =
  /** The trainee's turn. */
  | { kind: 'listening' }
  /** A line is on its way, the customer is answering. */
  | { kind: 'replying' }
  | { kind: 'ended'; end: CoachingPracticeEnd };

export interface PracticeSession {
  /** Settled lines, in speaking order. */
  lines$: BehaviorSubject<CoachingPracticeLine[]>;
  /** The customer's reply while it streams. */
  streaming$: BehaviorSubject<string>;
  phase$: BehaviorSubject<PracticePhase>;
  /** Practice mode only. */
  mood$: BehaviorSubject<CoachingCustomerMood | null>;
  /** Trainee lines left, including the next one. */
  linesLeft$: BehaviorSubject<number>;
  /** A line that could not be sent, to send again. */
  failedLine$: BehaviorSubject<string | null>;
  events: Subject<PracticeEvent>;
  /** The trainee line it put in the thread; null when nothing was sent. */
  send: (
    line: string,
    afterHint: boolean,
    /** A spoken line's parked voice, stored with it; null when typed. */
    voiceToken: string | null,
  ) => CoachingPracticeLine | null;
  /** False when the stop did not go through; the scene carries on. */
  stop: () => Promise<boolean>;
}

/**
 * One scene's conversation: sends lines and folds the streamed events into
 * subjects. `speak$` is the trainee's mute choice, read as each line goes.
 */
export function usePracticeSession(
  scene: PracticeScene,
  speak$: BehaviorSubject<boolean>,
): PracticeSession {
  const { coachingService } = useServiceFactory();
  const subjects = useCreation(
    () => ({
      lines$: new BehaviorSubject<CoachingPracticeLine[]>(scene.transcript),
      streaming$: new BehaviorSubject(''),
      phase$: new BehaviorSubject<PracticePhase>(
        scene.end ? { kind: 'ended', end: scene.end } : { kind: 'listening' },
      ),
      mood$: new BehaviorSubject<CoachingCustomerMood | null>(scene.mood),
      linesLeft$: new BehaviorSubject(scene.maxLines - scene.usedLines),
      failedLine$: new BehaviorSubject<string | null>(null),
      events: new Subject<PracticeEvent>(),
    }),
    [scene],
  );
  const connection = useRef<SSEConnection | null>(null);
  useEffect(() => () => connection.current?.abort(), []);

  const send = useCallback(
    (raw: string, afterHint: boolean, voiceToken: string | null) => {
      const line = raw.trim();
      if (!line || subjects.phase$.getValue().kind !== 'listening') return null;
      subjects.failedLine$.next(null);
      subjects.phase$.next({ kind: 'replying' });
      subjects.streaming$.next('');
      const pending: CoachingPracticeLine = {
        turn: 0,
        speaker: CoachingPracticeSpeaker.Trainee,
        text: line,
      };
      subjects.lines$.next([...subjects.lines$.getValue(), pending]);
      let ended: CoachingPracticeEnd | null = null;
      let answered = false;
      const settle = () =>
        subjects.phase$.next(
          ended ? { kind: 'ended', end: ended } : { kind: 'listening' },
        );
      const callbacks: CoachingPracticeTurnCallbacks = {
        onTurn: ({ turn }) => {
          pending.turn = turn;
          subjects.events.next({ kind: 'line_sent', turn });
        },
        onCustomerText: ({ text }) =>
          subjects.streaming$.next(subjects.streaming$.getValue() + text),
        onCustomerAudio: clip => subjects.events.next({ kind: 'audio', clip }),
        onCustomerDone: done => {
          answered = true;
          // The kept line wins over what streamed (a retried reply).
          subjects.lines$.next([
            ...subjects.lines$.getValue(),
            {
              turn: done.turn,
              speaker: CoachingPracticeSpeaker.Customer,
              text: done.say,
            },
          ]);
          subjects.streaming$.next('');
          subjects.linesLeft$.next(
            Math.max(0, subjects.linesLeft$.getValue() - 1),
          );
          subjects.events.next({ kind: 'customer_done', turn: done.turn });
        },
        onMood: ({ mood }) => subjects.mood$.next(mood),
        onEvidence: event =>
          subjects.events.next({ kind: 'evidence', ...event }),
        onReaction: reaction =>
          subjects.events.next({ kind: 'reaction', reaction }),
        onVerdict: ({ verdict }) =>
          subjects.events.next({ kind: 'verdict', verdict }),
        onEnded: end => {
          ended = end;
        },
        onDone: () => {
          connection.current = null;
          settle();
        },
        onError: message => {
          console.error('coaching practice turn failed', message);
          connection.current = null;
          subjects.streaming$.next('');
          if (!answered) {
            // Never answered: the line waits to be sent again, unchanged.
            subjects.lines$.next(
              subjects.lines$.getValue().filter(x => x !== pending),
            );
            subjects.failedLine$.next(line);
          }
          settle();
        },
      };
      const request = {
        line,
        afterHint,
        speak: speak$.getValue(),
        voiceToken,
      };
      const { target } = scene;
      connection.current =
        target.kind === 'practice'
          ? coachingService.practiceTurn(target.attemptId, request, callbacks)
          : coachingService.replayTurn(target.replayId, request, callbacks);
      return pending;
    },
    [coachingService, scene, speak$, subjects],
  );

  const stop = useCallback(async () => {
    const { target } = scene;
    if (target.kind !== 'practice') return true;
    connection.current?.abort();
    connection.current = null;
    try {
      const end = await coachingService.stopPractice(target.attemptId);
      subjects.phase$.next({ kind: 'ended', end });
      return true;
    } catch (error) {
      console.error('coaching practice stop failed', error);
      // The aborted turn fires nothing: hand the turn back.
      subjects.streaming$.next('');
      subjects.phase$.next({ kind: 'listening' });
      return false;
    }
  }, [coachingService, scene, subjects]);

  // One object per scene: consumers subscribe in effects keyed on it.
  return useMemo(() => ({ ...subjects, send, stop }), [subjects, send, stop]);
}
